import React, { FC, useEffect, useState } from 'react'
import Helmet from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { fetchSettingMeasureRestriction, updateSettingMeasureRestriction } from '../../../../states/containers/setting/systemSlice'
import { Link, useParams } from 'react-router-dom'
import { setPath } from '../../../../utils/url'
import { URL } from '../../../constants/url'
import { LangValue } from '../../../../states/types/common'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconName } from '@fortawesome/fontawesome-common-types'
import MemberListModule from '../../../components/common/MemberListModule'
import SettingModal from '../../../components/common/SettingModal'
import { RequestPatchSettingMeasureRestriction, SettingMeasureRestrictionMember } from '../../../../states/types/setting'
import LoadingSquare from '../../../components/common/LoadingSquare'
import { useAlert } from 'react-alert'
import { RootState } from '../../../../states/containers/store'

const STATUS_LIST = [
  {
    label: "制限する",
    value: true,
  },
  {
    label: "制限しない",
    value: false,
  },
]

const SELECT_LIST = [
  {
    label: "会社全体の設定に準拠する",
    value: "common",
  },
  {
    label: "制限する",
    value: "on",
  },
  {
    label: "制限しない",
    value: "off",
  },
]

const Index: FC = () => {
  const dispatch = useDispatch()
  const alert = useAlert()
  const { language } = useParams<{ language: LangValue }>()

  const {
    login,
  } = useSelector<RootState, RootState>((state: RootState) => (state))

  const [displayMenu, setDisplayMenu] = useState<boolean>(true)

  const [originMemberList, setOriginMemberList] = useState<SettingMeasureRestrictionMember[]>([])
  const [commonValue, setCommonValue] = useState<boolean>(true)

  const [isDisplayModal, setIsDisplayModal] = useState<boolean>(false)
  const [targetMembers, setTargetMembers] = useState<SettingMeasureRestrictionMember[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)

  const openUpdateModal = (memberIds: number[]) => {
    setTargetMembers(originMemberList.filter((v) => memberIds.includes(v.id)))
    setIsDisplayModal(true)
  }

  const closeModal = () => {
    setTargetMembers([])
    setIsDisplayModal(false)
  }

  const buildParam = (type: string) => {
    switch (type) {
      case "on":
        return true
      case "off":
        return false
      default:
        return null
    }
  }

  const buildParamToString = (type: boolean | null) => {
    if (type === null) {
      return "common"
    }
    if (type) return "on"
    return "off"
  }

  const changeMenuDisplay = (data: {commonFlg: boolean, memberIds?: number[], type: string}) => {
    const param: RequestPatchSettingMeasureRestriction = { change_common_setting: data.commonFlg }
    const restriction = buildParam(data.type)
    if (data.commonFlg) {
      param.restriction = restriction ?? true
    } else {
      param.member_ids = data.memberIds
      param.use_common_setting = restriction === null
      if (data.type !== "common") {
        param.restriction = restriction ?? true
      }
    }
    dispatch(updateSettingMeasureRestriction(
      param,
      () => {
        closeModal()
        if (data.commonFlg) {
          setCommonValue(restriction ?? true)
          alert.success("会社全体の設定を変更しました")
        } else {
          setOriginMemberList((cur) => cur.map((v) => (
            param.member_ids?.includes(v.id)
              ? { ...v, use_common_setting: param.use_common_setting || false, restriction: param.restriction ?? null }
              : v
          )))
          alert.success("ユーザーの設定を変更しました")
        }
      },
    ))
  }

  useEffect(() => {
    setIsLoading(true)
    dispatch(fetchSettingMeasureRestriction(
      (result) => {
        setOriginMemberList(result.members)
        setCommonValue(result.common.restriction)
        setDisplayMenu(result.common.restriction)
        setIsLoading(false)
      },
    ))
  }, [])

  return (
    <section className="setting_page">
      <Helmet>
        <title>{`Qasee - 設定 - ${login.role_name === "管理者" ? "管理者" : "共通"}設定 - 計測停止の制限設定`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.SETTING_COMMON.path, language)}
        >
          {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
        </Link>
        &nbsp;&gt;&nbsp;
        計測停止の制限設定
        &nbsp;
      </section>
      <section className="page_header">
        <h1>計測停止の制限設定</h1>
      </section>
      <section className="page_description">
        <p>
          QaseeClientの操作を制限して、ユーザによる計測停止を行えないようにできます。
        </p>
        <p>
          ※Windows版はバージョン1.8.0以降、macOS版は1.7.0以降で有効です
        </p>
      </section>
      {
        isLoading ? (
          <LoadingSquare>
            <>
              情報を取得しています…
            </>
          </LoadingSquare>
        ) : (
          <>
            {
              login.role_target_restriction || (
                <section className="page_contents content-area">
                  <div className="content-block">
                    <div className="block-header">
                      <div className="title">
                        会社全体の設定
                      </div>
                    </div>
                    <div className="block-detail">
                      <div
                        className={`graphical_check_box${displayMenu ? " selected" : ""}`}
                        onClick={() => setDisplayMenu(!displayMenu)}
                        role="none"
                      >
                        <FontAwesomeIcon
                          icon={['far', `${displayMenu ? 'check-square' : 'square'}` as IconName]}
                          className={`icon ${displayMenu ? "checked" : "no_check"}`}
                        />
                        <div>
                          <div className="check_box_title">
                            ユーザーによる計測停止を制限する
                          </div>
                          <div className="check_box_description">
                            QaseeClientの操作を制限して、ユーザによる計測停止を行えないようにできます
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="mt-4 button"
                        onClick={() => changeMenuDisplay({ commonFlg: true, type: displayMenu ? "on" : "off" })}
                      >
                        設定する
                      </button>
                    </div>
                  </div>
                </section>
              )
            }
            <MemberListModule
              originMemberList={originMemberList.map((v) => ({ ...v, value: v.restriction }))}
              statusList={STATUS_LIST}
              commonValue={commonValue}
              valueLabel="ユーザーによる計測停止制限"
              type="bool"
              modalOpenCallback={(memberIds) => openUpdateModal(memberIds)}
            />
            <SettingModal
              targetMemberList={targetMembers.map((v) => ({ ...v, value: buildParamToString(v.restriction) }))}
              selectList={SELECT_LIST}
              label="ユーザーによる計測停止制限"
              isDisplayModal={isDisplayModal}
              submitCallback={(data: { commonFlg: boolean; memberIds: number[]; type: string }) => changeMenuDisplay(data)}
              closeCallback={closeModal}
            />
          </>
        )
      }
    </section>
  )
}

export default Index
